// js/components/CosSideCart.js
import Delegate from 'ftdomdelegate';
import { Responsive, Accessibility, Animation } from '../utils.js';

class CosSideCart extends HTMLElement {
  constructor() {
    super();
    this.delegateRoot = new Delegate(document.documentElement);
    this.itemCount = window.theme.cartCount;
  }

  connectedCallback() {
    // Element is connected to DOM, now we can safely query for child elements
    this.options = JSON.parse(this.getAttribute('data-settings')) || {};
    this.isMiniCartOpen = false;
    this.miniCartToggleElement = document.querySelector('[aria-controls="mini-cart"]');
    
    if (!this.miniCartToggleElement) {
      console.warn('cos-side-cart: miniCartToggleElement not found');
      return;
    }
    
    this._attachListeners();
    this._checkAutoOpenCart();
  }

  disconnectedCallback() {
    this.delegateRoot.off();
  }

  _attachListeners() {
    this.delegateRoot.on("click", '[data-action="toggle-mini-cart"]', this._toggleMiniCart.bind(this));
    this.delegateRoot.on("click", '[data-action="close-mini-cart"]', this._closeMiniCart.bind(this));
    this.delegateRoot.on("keyup", this._checkMiniCartClose.bind(this));
    this.delegateRoot.on("click", this._onWindowClick.bind(this));
    this.delegateRoot.on("click", '[data-action="decrease-quantity"]', this._updateQuantity.bind(this));
    this.delegateRoot.on("click", '[data-action="increase-quantity"]', this._updateQuantity.bind(this));
    this.delegateRoot.on("change", ".quantity-selector:not(.quantity-selector--product) .quantity-selector__value", this._updateQuantity.bind(this));
    this.delegateRoot.on("keyup", ".quantity-selector:not(.quantity-selector--product) .quantity-selector__value", this._updateQuantitySize.bind(this));
    this.delegateRoot.on("keydown", ".quantity-selector__value", this._blockEnterKey.bind(this));
    this.delegateRoot.on("product:added", this._onProductAdded.bind(this));
    this.delegateRoot.on("cart:refresh", this._onCartRefresh.bind(this));
  }

  _toggleMiniCart(event2) {
    if (event2) {
      event2.preventDefault();
    }
    if (this.isMiniCartOpen) {
      this._closeMiniCart();
    } else {
      this._openMiniCart();
    }
  }

  _openMiniCart() {
    if (this.miniCartToggleElement) {
      this.miniCartToggleElement.setAttribute("aria-expanded", "true");
      this.miniCartToggleElement.classList.add("is-open");
      if (Responsive.getCurrentBreakpoint() === "phone") {
        let cartIcon = this.miniCartToggleElement.querySelector(".header__cart-icon");
        if (cartIcon) {
          cartIcon.setAttribute("aria-expanded", "true");
        }
      }
    }
    if (this) {
      this.setAttribute("aria-hidden", "false");
      this.removeAttribute("inert");
      this.isMiniCartOpen = true;
      Accessibility.trapFocus(this, "mini-cart");
      // document.body.classList.add("no-mobile-scroll");
    }
  }

  _closeMiniCart() {
    if (this.miniCartToggleElement) {
      this.miniCartToggleElement.setAttribute("aria-expanded", "false");
      this.miniCartToggleElement.classList.remove("is-open");
      if (Responsive.getCurrentBreakpoint() === "phone") {
        let cartIcon = this.miniCartToggleElement.querySelector(".header__cart-icon");
        if (cartIcon) {
          cartIcon.setAttribute("aria-expanded", "false");
        }
      }
    }
    if (this) {
      this.setAttribute("aria-hidden", "true");
      this.setAttribute("inert", "");
      this.isMiniCartOpen = false;
      // document.body.classList.remove("no-mobile-scroll");
    }
  }

  _checkMiniCartClose(event2) {
    if (!this.isMiniCartOpen) {
      return;
    }
    if (event2.key === "Escape") {
      this._closeMiniCart();
    }
  }


  /**
   * Change the quantity of the cart
   */
  _updateQuantity(event2, target) {
    let parsedQuantity = 1;
    if (target.tagName === "INPUT") {
      parsedQuantity = parseInt(target.value);
    } else {
      parsedQuantity = parseInt(target.getAttribute("data-quantity"));
    }
    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    fetch(`${window.routes.cartChangeUrl}.js`, {
      body: JSON.stringify({
        line: target.getAttribute("data-line"),
        quantity: parsedQuantity
      }),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
        // This is needed as currently there is a bug in Shopify that assumes this header
      }
    }).then((response) => {
      if (response.ok) {
        if (theme.pageType === "cart") {
          window.location.reload();
        } else {
          response.json().then((content) => {
            this.itemCount = content["item_count"];
            this._rerender().then(() => {
              document.dispatchEvent(new CustomEvent("theme:loading:end"));
            });
          });
        }
      } else {
        response.json().then((content) => {
          let messageElement = document.createElement("div");
          messageElement.className = "cart__status-message cart__status-message--header";
          messageElement.innerHTML = `<div class="alert alert--error"><p class="container">${content["description"]}</p></div>`;
          document.querySelector(".header")?.append(messageElement);
          Animation.slideDown(messageElement);
          setTimeout(function() {
            Animation.slideUp(messageElement, () => {
              messageElement.remove();
            });
          }, 5500);
          this._rerender().then(() => {
            document.dispatchEvent(new CustomEvent("theme:loading:end"));
          });
        });
      }
    });
    event2.preventDefault();
  }

  _updateQuantitySize(event2, target) {
    target.setAttribute("size", Math.max(target.value.length, 2));
  }

  _blockEnterKey(event2) {
    if (event2.key === "Enter") {
      return false;
    }
  }

  /**
   * This method is called internally to rerender the cart, based on the content returned by Shopify Ajax API.
   * We could save some performance by updating directly in JavaScript instead of doing a GET call to get the HTML
   * from Shopify, but by experience, this allows for easier app integration as it allows the Liquid to re-run
   * all the time and hence having easier logic.
   */
  _rerender() {
    return fetch(`${window.routes.cartUrl}?section_id=side-cart`, {
      credentials: "same-origin",
      method: "GET",
      headers: {
        "Cache-Control": "no-cache"
      }
    }).then((content) => {
      content.text().then((html) => {
        const myDiv = document.createElement("div");
        myDiv.innerHTML = html;
        const firstChild = myDiv.firstElementChild;
        if (firstChild && firstChild.hasAttribute("data-item-count")) {
          this.itemCount = parseInt(firstChild.getAttribute("data-item-count"));
        }
        const cartCountElement = document.querySelector(".header__cart-count");
        if (cartCountElement) {
          cartCountElement.textContent = this.itemCount;
        }
        let tempElement = document.createElement("div");
        tempElement.innerHTML = html;
        let miniCartItemListElement = this?.querySelector(".mini-cart__line-item-list"), scrollPosition = null;
        if (miniCartItemListElement) {
          scrollPosition = miniCartItemListElement.scrollTop;
        }
        this.innerHTML = tempElement.querySelector('cos-side-cart').innerHTML;
        let newMiniCartItemListElement = this.querySelector(".mini-cart__line-item-list");
        if (newMiniCartItemListElement && scrollPosition !== null) {
          newMiniCartItemListElement.scrollTop = scrollPosition;
        }
        this.dispatchEvent(new CustomEvent("cart:rerendered"));
      });
    });
  }


  /**
   * This callback is automatically called when a variant has been added, which allows us to open it and re-render
   */
  _onProductAdded(event2) {
    this.itemCount += event2.detail.quantity;
    this._onCartRefresh().then(() => {
      if (event2.detail.productAdded) {
        if (window.theme.cartType === "message" && event2.detail.button) {
          const originalMessage = event2.detail.button.innerHTML;
          event2.detail.button.innerHTML = window.languages.productAddedShort;
          setTimeout(() => {
            event2.detail.button.innerHTML = originalMessage;
          }, 1500);
        }
        if (window.theme.cartType === "drawer") {
          this._openMiniCart();
        }
      }
    });
  }

  /**
   * Allows to refresh the mini-cart
   */
  _onCartRefresh() {
    return this._rerender().then(() => {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    });
  }

  /**
   * We need to catch click outside the element to automatically close mini-cart
   */
  _onWindowClick(event2) {
    // Don't close if click is on the cart toggle button
    if (event2.target.closest('[data-action="toggle-mini-cart"]')) {
      return;
    }
    
    // Don't close if click is inside the cart component
    if (this && this.isMiniCartOpen && !this.contains(event2.target)) {
      this._closeMiniCart();
    }
  }

  /**
   * Check if cart should be opened automatically based on URL parameter
   */
  _checkAutoOpenCart() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('cart_drawer') === 'open' && this) {
      // Use setTimeout to ensure DOM is fully loaded
      setTimeout(() => {
        this._openMiniCart();
        // Remove the parameter from URL without page reload
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('cart_drawer');
        window.history.replaceState({}, '', newUrl);
      }, 100);
    }
  }
}

if (!customElements.get('cos-side-cart')) customElements.define('cos-side-cart', CosSideCart);
